<script lang="ts">
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import { RICH_TOOLTIP_CLASS } from "../../../../../../../../../../../_shared/_logic/rich-tooltips"
  import type {
    BoardEvent,
    BoardRouteConnector,
    BoardRouteEdge,
  } from "../../../../../../../../_logic/calendar-types"
  import { buildRouteConnectors } from "./_logic/routeConnectors"
  import {
    ROUTE_ARROW_SIZE_PX,
    ROUTE_HIT_STROKE_PX,
    ROUTE_VISUAL_STROKE_PX,
  } from "../../_shared/_logic/route-constants"

  const {
    edges = [],
    events = [],
    hiddenEventId = null,
    viewHeight = 0,
    viewWidth = null,
    scopeId = "calendar-route",
  }: {
    edges?: BoardRouteEdge[]
    events?: BoardEvent[]
    hiddenEventId?: string | null
    viewHeight?: number
    viewWidth?: number | null
    scopeId?: string
  } = $props()

  let overlayEl = $state<SVGSVGElement | null>(null)
  let measuredWidth = $state(0)

  $effect(() => {
    if (viewWidth != null) return

    const element = overlayEl

    if (!element) return

    const updateMeasuredWidth = () => {
      measuredWidth = element.getBoundingClientRect().width
    }

    updateMeasuredWidth()

    if (typeof ResizeObserver === "undefined") return

    const observer = new ResizeObserver(updateMeasuredWidth)
    observer.observe(element)

    return () => observer.disconnect()
  })

  const activeWidth = $derived(viewWidth ?? measuredWidth)
  const connectors = $derived<BoardRouteConnector[]>(
    activeWidth > 0
      ? buildRouteConnectors({
          edges,
          events,
          hiddenEventId,
          totalWidthPx: activeWidth,
        })
      : [],
  )
  const normalizedWidth = $derived(Math.max(1, Math.round(activeWidth)))
  const normalizedHeight = $derived(Math.max(1, Math.round(viewHeight)))
  const markerId = (grade: string) =>
    `${scopeId}-route-arrow-${String(grade || "UNKNOWN").toLowerCase()}`
  const gradeClass = (connector: BoardRouteConnector) =>
    `grade-${String(connector.routeGrade || "UNKNOWN").toLowerCase()}`
</script>

<svg
  bind:this={overlayEl}
  class="route-connectors"
  viewBox={`0 0 ${normalizedWidth} ${normalizedHeight}`}
  preserveAspectRatio="none"
  aria-label="Routenverbindungen"
  style={`--route-stroke:${ROUTE_VISUAL_STROKE_PX};--route-hit-stroke:${ROUTE_HIT_STROKE_PX};--route-arrow-size:${ROUTE_ARROW_SIZE_PX};--route-unknown-stroke:var(--cal-text,var(--text-invers-100,#2f4858));`}
>
  <defs>
    <marker
      id={markerId("GREEN")}
      markerUnits="userSpaceOnUse"
      viewBox="0 0 6 6"
      refX="5.4"
      refY="3"
      markerWidth={ROUTE_ARROW_SIZE_PX}
      markerHeight={ROUTE_ARROW_SIZE_PX}
      orient="auto"
    >
      <path d="M 0 0 L 6 3 L 0 6 z" class="marker marker-green"></path>
    </marker>
    <marker
      id={markerId("YELLOW")}
      markerUnits="userSpaceOnUse"
      viewBox="0 0 6 6"
      refX="5.4"
      refY="3"
      markerWidth={ROUTE_ARROW_SIZE_PX}
      markerHeight={ROUTE_ARROW_SIZE_PX}
      orient="auto"
    >
      <path d="M 0 0 L 6 3 L 0 6 z" class="marker marker-yellow"></path>
    </marker>
    <marker
      id={markerId("RED")}
      markerUnits="userSpaceOnUse"
      viewBox="0 0 6 6"
      refX="5.4"
      refY="3"
      markerWidth={ROUTE_ARROW_SIZE_PX}
      markerHeight={ROUTE_ARROW_SIZE_PX}
      orient="auto"
    >
      <path d="M 0 0 L 6 3 L 0 6 z" class="marker marker-red"></path>
    </marker>
    <marker
      id={markerId("UNKNOWN")}
      markerUnits="userSpaceOnUse"
      viewBox="0 0 6 6"
      refX="5.4"
      refY="3"
      markerWidth={ROUTE_ARROW_SIZE_PX}
      markerHeight={ROUTE_ARROW_SIZE_PX}
      orient="auto"
    >
      <path d="M 0 0 L 6 3 L 0 6 z" class="marker marker-unknown"></path>
    </marker>
  </defs>
  {#each connectors as connector (connector.id)}
    <path
      class={`route-connector ${gradeClass(connector)}`}
      class:compact={connector.compact}
      class:dashed={connector.dashed}
      class:missing={connector.missingCoordinates}
      d={connector.path}
      marker-end={`url(#${markerId(connector.routeGrade)})`}
      aria-hidden="true"
    ></path>
    <path
      class="route-connector-hit"
      d={connector.hitPath}
      aria-label={connector.ariaLabel}
      use:tooltip={connector.tooltip
        ? { content: connector.tooltip, className: RICH_TOOLTIP_CLASS }
        : undefined}
    ></path>
  {/each}
</svg>

<style lang="less">
  .route-connectors {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    pointer-events: none;
    z-index: 1;
  }

  .route-connector {
    fill: none;
    stroke: var(--success-100, #0f8f4d);
    stroke-width: var(--route-stroke);
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: 0.62;
    vector-effect: non-scaling-stroke;

    &.compact {
      stroke-width: calc(var(--route-stroke) + 0.5);
      opacity: 0.66;
    }

    &.dashed {
      stroke-dasharray: 4 3;
    }

    &.grade-yellow {
      stroke: var(--warning-100, #d97706);
    }

    &.grade-red {
      stroke: var(--error-100, #dc2626);
    }

    &.grade-unknown,
    &.missing {
      stroke: var(--route-unknown-stroke);
      stroke-dasharray: 2 3;
      opacity: 0.82;
    }
  }

  .route-connector-hit {
    fill: none;
    stroke: transparent;
    stroke-width: var(--route-hit-stroke);
    pointer-events: stroke;
    vector-effect: non-scaling-stroke;
  }

  .marker {
    &.marker-green {
      fill: var(--success-100, #0f8f4d);
    }

    &.marker-yellow {
      fill: var(--warning-100, #d97706);
    }

    &.marker-red {
      fill: var(--error-100, #dc2626);
    }

    &.marker-unknown {
      fill: var(--route-unknown-stroke);
    }
  }
</style>
